import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";

export default function AdminNewsFormPage({
                                              primaryColor = "#8D6E63",
                                              secondaryColor = "#A1887F",
                                          }) {
    const navigate = useNavigate();
    const { id } = useParams();

    const isEditing = Boolean(id);

    const [title, setTitle] = useState("");
    const [excerpt, setExcerpt] = useState("");
    const [content, setContent] = useState("");
    const [published, setPublished] = useState(false);

    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!isEditing) return;

        async function loadNewsItem() {
            setIsLoading(true);
            setErrorMessage("");

            const { data, error } = await supabase
                .from("news")
                .select("*")
                .eq("id", id)
                .single();

            if (error) {
                console.error("Fehler beim Laden des Beitrags:", error);
                setErrorMessage("Der Beitrag konnte nicht geladen werden.");
                setIsLoading(false);
                return;
            }

            setTitle(data.title || "");
            setExcerpt(data.excerpt || "");
            setContent(data.content || "");
            setPublished(Boolean(data.published));

            setIsLoading(false);
        }

        loadNewsItem();
    }, [id, isEditing]);

    async function handleSubmit(event) {
        event.preventDefault();

        if (!title.trim()) {
            setErrorMessage("Bitte gib einen Titel ein.");
            return;
        }

        if (!excerpt.trim()) {
            setErrorMessage("Bitte gib eine kurze Zusammenfassung ein.");
            return;
        }

        if (!content.trim()) {
            setErrorMessage("Bitte gib einen Beitragstext ein.");
            return;
        }

        setIsSaving(true);
        setErrorMessage("");

        const payload = {
            title: title.trim(),
            excerpt: excerpt.trim(),
            content: content.trim(),
            published,
            published_at: published ? new Date().toISOString() : null,
            updated_at: new Date().toISOString(),
        };

        if (isEditing) {
            const { error } = await supabase
                .from("news")
                .update(payload)
                .eq("id", id);

            setIsSaving(false);

            if (error) {
                console.error("Fehler beim Speichern:", error);
                setErrorMessage("Der Beitrag konnte nicht gespeichert werden.");
                return;
            }

            navigate("/admin/news");
            return;
        }

        const { error } = await supabase
            .from("news")
            .insert([
                {
                    ...payload,
                    created_at: new Date().toISOString(),
                },
            ]);

        setIsSaving(false);

        if (error) {
            console.error("Fehler beim Erstellen:", error);
            setErrorMessage("Der Beitrag konnte nicht erstellt werden.");
            return;
        }

        navigate("/admin/news");
    }

    if (isLoading) {
        return (
            <main style={adminStyles.dashboardPage}>
                <div style={adminStyles.dashboardContainer}>
                    <p style={{ color: "#666" }}>Beitrag wird geladen...</p>
                </div>
            </main>
        );
    }

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            {isEditing ? "Beitrag bearbeiten" : "Neuer Beitrag"}
                        </h1>
                        <p style={adminStyles.subtitle}>
                            Schreibe eine Neuigkeit für die Webseite. Du kannst sie als
                            Entwurf speichern oder direkt veröffentlichen.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate("/admin/news")}
                        style={{
                            ...adminStyles.secondaryButton,
                            backgroundColor: secondaryColor,
                        }}
                    >
                        Zurück
                    </button>
                </header>

                <motion.form
                    onSubmit={handleSubmit}
                    style={{
                        ...adminStyles.dashboardCard,
                        maxWidth: "850px",
                    }}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {errorMessage && (
                        <div style={adminStyles.error}>{errorMessage}</div>
                    )}

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="title">
                            Titel
                        </label>
                        <input
                            id="title"
                            type="text"
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                            style={adminStyles.input}
                            placeholder="z. B. Wir planen unseren nächsten Wurf"
                        />
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="excerpt">
                            Kurzbeschreibung
                        </label>
                        <textarea
                            id="excerpt"
                            value={excerpt}
                            onChange={(event) => setExcerpt(event.target.value)}
                            style={{
                                ...adminStyles.input,
                                minHeight: "110px",
                                resize: "vertical",
                                lineHeight: 1.6,
                                fontFamily: "inherit",
                            }}
                            placeholder="Kurzer Text, der auf der Webseite in der Karte angezeigt wird."
                        />
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="content">
                            Vollständiger Beitrag
                        </label>
                        <textarea
                            id="content"
                            value={content}
                            onChange={(event) => setContent(event.target.value)}
                            style={{
                                ...adminStyles.input,
                                minHeight: "260px",
                                resize: "vertical",
                                lineHeight: 1.7,
                                fontFamily: "inherit",
                            }}
                            placeholder="Hier kommt der ausführliche Text hinein, der später im Modal angezeigt wird."
                        />
                    </div>

                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.7rem",
                            marginBottom: "1.5rem",
                            cursor: "pointer",
                            color: "#444",
                            lineHeight: 1.5,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={published}
                            onChange={(event) =>
                                setPublished(event.target.checked)
                            }
                            style={{
                                width: "18px",
                                height: "18px",
                                accentColor: primaryColor,
                            }}
                        />
                        Beitrag direkt veröffentlichen
                    </label>

                    <div
                        style={{
                            display: "flex",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                        }}
                    >
                        <motion.button
                            type="submit"
                            disabled={isSaving}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                opacity: isSaving ? 0.7 : 1,
                            }}
                            whileHover={{
                                scale: isSaving ? 1 : 1.03,
                                backgroundColor: secondaryColor,
                            }}
                            whileTap={{ scale: isSaving ? 1 : 0.97 }}
                        >
                            {isSaving
                                ? "Wird gespeichert..."
                                : isEditing
                                    ? "Änderungen speichern"
                                    : "Beitrag erstellen"}
                        </motion.button>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/news")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: "#777",
                            }}
                        >
                            Abbrechen
                        </button>
                    </div>
                </motion.form>
            </div>
        </main>
    );
}